// Given a valid eligibility search results object, transforms it into a format for display on the frontend.

import { Column } from "react-table";
import EligibilityQueryResults, {
  BenefitsDetails,
  CallDetails,
  Maximums,
  PlanInformation,
  ProcedureCategory,
  ProcedureCodeInfo,
  TreatmentHistory,
} from "./EligibilityQueryResults";
import ClaimsResults, {
  ClaimInfoUnion,
} from "./ClaimsResults";
import { Section, TableResults } from "./TableResults";
import {
  PLACEHOLDER_COLUMN,
} from "./EligibilityQueryResultsColumns";
import {
  formatDateTime,
  formatDate,
  varNameToTitle,
  formatCell,
} from "utils/utils";

const DATE_TIME_FIELDS = new Set<string>(["call_end_time"]);
const DATE_FIELDS = new Set<string>([
  "effective_date",
  "termination_date",
  "date_of_service",
  "date_received",
  "date_processed_up_to",
  "date_processed",
  "date_paid"
]);
export const customDatesJsonStringParser = function (
  key: string,
  value: string
) {
  if (DATE_TIME_FIELDS.has(key)) {
    return formatDateTime(value);
  }
  if (DATE_FIELDS.has(key)) {
    return formatDate(value);
  }
  return value;
};

const GENERIC_SECTIONS = [
  "call_details",
  "maximums",
  "extra_maximums",
  "plan_information",
  "claims_information",
  "extra_dental_info",
  "extra_medical_info",
];
// Transforms results from a eligibility query into a format for display on the frontend.
export const transformEligibilityQueryResults = function (
  eligibility_query_results: EligibilityQueryResults
): TableResults {
  // Handle null/undefined input
  if (!eligibility_query_results) {
    return { sections: [] };
  }

  // Keeping track of specialties like this isn't great, we should ideally be able to determine this elsewhere or pass it from the backend.
  const is_dental_inquiry = eligibility_query_results.extra_dental_info != null;
  var sections = [];
  // Generic sections are hard coded as the sections from the eligibility query results.
  for (const section_name of GENERIC_SECTIONS) {
    const partial_results_section =
      eligibility_query_results[section_name as keyof EligibilityQueryResults];
    if (partial_results_section == null) {
      continue;
    }
    if (section_name === "call_details") {
      const call_details_array = partial_results_section as CallDetails[];
      for (const call_detail of call_details_array) {
        const section = createGenericSection(
          call_detail,
          varNameToTitle(section_name),
          is_dental_inquiry
        );
        sections.push(section);
      }
    } else {
      const section = createGenericSection(
        partial_results_section,
        varNameToTitle(section_name),
        is_dental_inquiry
      );
      sections.push(section);
    }
  }
  // Special case for procedure codes, categories and treatment history.
  if (eligibility_query_results.categories) {
    const categories_section = createCategoriesSection(
      eligibility_query_results.categories,
      is_dental_inquiry
    );
    sections.push(categories_section);
  }
  if (eligibility_query_results.procedure_codes) {
    const procedure_codes_sections = createProcedureCodesSections(
      eligibility_query_results.procedure_codes,
      is_dental_inquiry
    );
    sections.push(...procedure_codes_sections);
  }
  if (eligibility_query_results.treatment_history) {
    const treatment_history_section = createTreatmentHistorySection(
      eligibility_query_results.treatment_history
    );
    sections.push(treatment_history_section);
  }
  const transformedResults: TableResults = {
    sections: sections,
  };
  return transformedResults;
};

// Transforms results from a claims query into a format for display on the frontend.
export const transformClaimsResults = function (
  claims_results: ClaimsResults
): TableResults {
  var sections = [];

  // Add call details sections
  if (claims_results.call_details && claims_results.call_details.length > 0) {
    for (const call_detail of claims_results.call_details) {
      const section = createGenericSection(
        call_detail,
        varNameToTitle("call_details"),
        false // claims are not dental-specific
      );
      sections.push(section);
    }
  }

  // Add status section
  const statusSection = createGenericSection(
    { status: claims_results.status },
    "Claim Status",
    false
  );
  sections.push(statusSection);

  // Add similar claim dates section if available
  if (claims_results.similar_claim_dates && claims_results.similar_claim_dates.length > 0) {
    const similarDatesSection = createSimilarClaimDatesSection(
      claims_results.similar_claim_dates
    );
    sections.push(similarDatesSection);
  }

  // Add individual claim sections
  if (claims_results.claims_info && claims_results.claims_info.length > 0) {
    for (let i = 0; i < claims_results.claims_info.length; i++) {
      const claim_info = claims_results.claims_info[i];
      const section = createClaimInfoSection(
        claim_info,
        `Claim ${i + 1}`,
        i + 1
      );
      sections.push(section);
    }
  }

  // Add claim-not-found details when the payer has no claim on file for the DOS.
  if (claims_results.claim_not_found_info) {
    const claimNotFoundSection = createGenericSection(
      claims_results.claim_not_found_info,
      "Claim Not Found",
      false
    );
    sections.push(claimNotFoundSection);
  }

  const transformedResults: TableResults = {
    sections: sections,
  };
  return transformedResults;
};

// Safely serialize a value, handling circular references
const safeSerialize = (value: any): string => {
  try {
    const seen = new WeakSet();
    return JSON.stringify(value, (_, val) => {
      if (val != null && typeof val === "object") {
        if (seen.has(val)) return "[Circular]";
        seen.add(val);
      }
      return val;
    }, 2);
  } catch {
    return "[Unable to serialize]";
  }
};

// Serialize nested objects in row data to prevent react-table issues
const serializeRowData = (obj: any, excludeKeys: string[] = []): any => {
  const result: any = {};
  for (const key in obj) {
    if (excludeKeys.includes(key)) continue;
    const value = obj[key];
    if (value == null) {
      result[key] = null;
    } else if (typeof value === "object" && !Array.isArray(value) && !(value instanceof Date)) {
      result[key] = safeSerialize(value);
    } else {
      result[key] = value;
    }
  }
  return result;
};

const createGenericSection = function (
  object: Object,
  section_name: string,
  is_dental_inquiry: boolean = false
): Section<Object> {
  const custom_answers = (object as any).custom_answers as Record<string, string> | null | undefined;
  const { custom_answers: _, ...objectWithoutCustomAnswers } = object as any;

  let rowObject = serializeRowData(objectWithoutCustomAnswers);

  // Filter out keys where value is null or empty string - only show fields with actual data
  const columns: Array<Column<Object>> = Object.keys(object)
    .filter((key) => {
      if (key === "custom_answers") return false;
      // Exclude 'name' field as it's used for section title in maximums
      if (key === "name") return false;
      const value = (object as any)[key];
      // Keep if value is not null and not empty string
      return value != null && value !== "";
    })
    .map((key) => ({
      Header: varNameToTitle(key),
      accessor: key as keyof Object,
      Cell: ({ value }: { value: any }) => {
        try {
          if (value == null) return "-";
          if (value === "") return "-";

          if (typeof value === "string") {
            return value;
          }

          if (typeof value === "object") {
            if (Array.isArray(value)) {
              if (value.length === 0) return "-";
              if (value.some(item => typeof item === "object" && item !== null)) {
                return safeSerialize(value);
              }
              return value.join(", ");
            }
            if (value instanceof Date) return formatCell(key, value.toISOString());
            return safeSerialize(value);
          }

          // Primitives (number, boolean)
          if (typeof value === "number" || typeof value === "boolean") {
            return formatCell(key, value);
          }

          // Fallback
          return String(value);
        } catch (e) {
          console.error(`Error rendering cell for key "${key}":`, e);
          return "-";
        }
      },
    }));
  // TODO(alan): remove this special casing for cleaner code.
  // Special case termination date because "null" termination date should still be recorded.
  if (section_name === "Plan Information") {
    var planInformation: PlanInformation = object as PlanInformation;
    if (planInformation["termination_date"] == null) {
      columns.push({
        Header: "Termination Date",
        accessor: "termination_date" as keyof Object,
      });
      (rowObject as any).termination_date = "None";
    }
  } else if (section_name === "Maximums" || section_name === "Extra Maximums") {
    // Use the name from maximums object for section title
    var maximums: Maximums = object as Maximums;
    if (maximums.name) {
      section_name = maximums.name;
    }
  }

  const rows = [rowObject];

  const section: Section<Object> = {
    title: section_name,
    columns: columns,
    rows: rows,
    custom_answers: custom_answers && Object.keys(custom_answers).length > 0 ? custom_answers : undefined,
  };
  return section;
};

const createProcedureCodesSections = function (
  procedure_codes: Array<[ProcedureCodeInfo, BenefitsDetails]>,
  is_dental_inquiry: boolean = false
): Array<Section<BenefitsDetails & ProcedureCodeInfo>> {
  // Clear benefit details when not covered
  for (const [, benefits_details] of procedure_codes) {
    if (benefits_details.is_covered === false) {
      benefits_details.copay_amount = null;
      benefits_details.coverage_percentage = null;
      benefits_details.coinsurance_percentage = null;
      benefits_details.allowed_fee = null;
      benefits_details.frequency_limitations = null;
      benefits_details.is_prior_auth_required = null;
      benefits_details.is_deductible_waived = null;
      benefits_details.prior_auth_info = null;
      benefits_details.category = null;
      benefits_details.age_limit = null;
      benefits_details.is_downgraded = null;
      benefits_details.downgraded_info = null;
    }
    if (benefits_details.coinsurance_percentage != null && is_dental_inquiry) {
      benefits_details.coverage_percentage = 100 - benefits_details.coinsurance_percentage;
      benefits_details.coinsurance_percentage = null;
    }
  }

  const procedure_name_to_procedure_info_and_benefit_details = new Map<
    string,
    Array<[ProcedureCodeInfo, BenefitsDetails]>
  >();
  for (const [procedure_code_info, benefits_details] of procedure_codes) {
    var procedure_name =
      procedure_code_info.procedure_name || "Other Procedures";
    var maybe_array_of_procedure_info_and_benefit_details =
      procedure_name_to_procedure_info_and_benefit_details.get(procedure_name);
    if (maybe_array_of_procedure_info_and_benefit_details != null) {
      maybe_array_of_procedure_info_and_benefit_details.push([
        procedure_code_info,
        benefits_details,
      ]);
    } else {
      procedure_name_to_procedure_info_and_benefit_details.set(procedure_name, [
        [procedure_code_info, benefits_details],
      ]);
    }
  }

  var sections = [];
  for (const [procedure_name, procedure_info_and_benefit_details] of procedure_name_to_procedure_info_and_benefit_details) {
    const rows: any[] = [];
    const procedure_code_custom_answers: Array<{
      procedure_code: string;
      custom_answers: Record<string, string>;
    }> = [];

    for (const [procedure_code_info, benefits_details] of procedure_info_and_benefit_details) {
      const { custom_answers: _, ...benefitsWithoutCustomAnswers } = benefits_details as any;
      const serializedBenefits = serializeRowData(benefitsWithoutCustomAnswers);

      rows.push({
        ...procedure_code_info,
        ...serializedBenefits,
        row_override: benefits_details.is_covered === false
          ? "No Coverage" + (benefits_details.more_info ? `: ${benefits_details.more_info}` : "")
          : null,
      });

      if (benefits_details.custom_answers && Object.keys(benefits_details.custom_answers).length > 0) {
        procedure_code_custom_answers.push({
          procedure_code: procedure_code_info.procedure_code,
          custom_answers: benefits_details.custom_answers,
        });
      }
    }

    // Get all unique keys from all rows that have non-null, non-empty values
    const keysWithValues = new Set<string>();
    rows.forEach(row => {
      Object.keys(row).forEach(key => {
        if (key !== "custom_answers" && key !== "row_override") {
          const value = row[key];
          if (value != null && value !== "") {
            keysWithValues.add(key);
          }
        }
      });
    });

    // Create columns dynamically from keys with values
    const columns: Array<Column<BenefitsDetails & ProcedureCodeInfo>> = Array.from(keysWithValues).map(key => ({
      Header: varNameToTitle(key),
      accessor: key as keyof (BenefitsDetails & ProcedureCodeInfo),
      Cell: ({ value }: { value: any }) => {
        try {
          if (value == null) return "-";
          if (value === "") return "-";
          if (typeof value === "string") return value;
          if (typeof value === "object") {
            if (Array.isArray(value)) {
              if (value.length === 0) return "-";
              if (value.some(item => typeof item === "object" && item !== null)) {
                return safeSerialize(value);
              }
              return value.join(", ");
            }
            if (value instanceof Date) return formatCell(key, value.toISOString());
            return safeSerialize(value);
          }

          // Primitives
          if (typeof value === "number" || typeof value === "boolean") {
            return formatCell(key, value);
          }

          return String(value);
        } catch {
          return "-";
        }
      },
    }));

    // Ensure procedure_code is first if present
    const pcIndex = columns.findIndex(c => c.accessor === "procedure_code");
    if (pcIndex > 0) {
      const [pcCol] = columns.splice(pcIndex, 1);
      columns.unshift(pcCol);
    }

    if (columns.length === 0) {
      columns.push(PLACEHOLDER_COLUMN as Column<BenefitsDetails & ProcedureCodeInfo>);
    }

    const section: Section<BenefitsDetails & ProcedureCodeInfo> = {
      title: varNameToTitle(procedure_name),
      columns: columns,
      rows: rows,
      procedure_code_custom_answers: procedure_code_custom_answers.length > 0 ? procedure_code_custom_answers : undefined,
    };
    sections.push(section);
  }
  return sections;
};

const createCategoriesSection = function (
  categories: Record<ProcedureCategory, BenefitsDetails>,
  is_dental_inquiry: boolean = false
): Section<BenefitsDetails> {
  for (const [procedure_category, benefits_details] of Object.entries(
    categories
  )) {
    if (benefits_details.is_covered === false) {
      benefits_details.copay_amount = null;
      benefits_details.coinsurance_percentage = null;
      benefits_details.frequency_limitations = null;
      benefits_details.is_prior_auth_required = null;
      benefits_details.is_deductible_waived = null;
      benefits_details.prior_auth_info = null;
      benefits_details.category = null;
      benefits_details.age_limit = null;
      benefits_details.is_downgraded = null;
      benefits_details.downgraded_info = null;

      continue;
    }
    if (benefits_details.coinsurance_percentage != null && is_dental_inquiry) {
      benefits_details.coverage_percentage = 100 - benefits_details.coinsurance_percentage;
      benefits_details.coinsurance_percentage = null;
    }
    benefits_details.category = procedure_category as ProcedureCategory;
  }

  const rows: any[] = [];
  const aggregated_custom_answers: Record<string, string> = {};

  for (const [category, benefits_details] of Object.entries(categories)) {
    const { custom_answers: _, ...benefitsWithoutCustomAnswers } = benefits_details as any;
    const serializedBenefits = serializeRowData(benefitsWithoutCustomAnswers);

    rows.push({
      ...serializedBenefits,
      category: category as ProcedureCategory,
      row_override: benefits_details.is_covered === false
        ? "No Coverage" + (benefits_details.more_info ? `: ${benefits_details.more_info}` : "")
        : null,
    });

    if (benefits_details.custom_answers) {
      Object.assign(aggregated_custom_answers, benefits_details.custom_answers);
    }
  }

  // Get all unique keys from all rows that have non-null, non-empty values
  const keysWithValues = new Set<string>();
  rows.forEach(row => {
    Object.keys(row).forEach(key => {
      if (key !== "custom_answers" && key !== "row_override") {
        const value = row[key];
        if (value != null && value !== "") {
          keysWithValues.add(key);
        }
      }
    });
  });

  // Create columns dynamically from keys with values
  const columns: Array<Column<BenefitsDetails>> = Array.from(keysWithValues).map(key => ({
    Header: varNameToTitle(key),
    accessor: key as keyof BenefitsDetails,
    Cell: ({ value }: { value: any }) => {
      try {
        if (value == null) return "-";
        if (value === "") return "-";
        if (typeof value === "boolean") return formatCell(key, value);
        if (typeof value === "number") return formatCell(key, value);
        if (typeof value === "string") return value;
        return String(value);
      } catch {
        return "-";
      }
    },
  }));

  // Ensure category is first if present
  const catIndex = columns.findIndex(c => c.accessor === "category");
  if (catIndex > 0) {
    const [catCol] = columns.splice(catIndex, 1);
    columns.unshift(catCol);
  }

  if (columns.length === 0) {
    columns.push(PLACEHOLDER_COLUMN);
  }

  const section: Section<BenefitsDetails> = {
    title: "Categories",
    columns: columns,
    rows: rows,
    custom_answers: Object.keys(aggregated_custom_answers).length > 0 ? aggregated_custom_answers : undefined,
  };
  return section;
};

const createTreatmentHistorySection = function (
  treatment_history: Record<string, Array<string>>
): Section<TreatmentHistory> {
  const historyObjects: TreatmentHistory[] = [];
  for (const [date, procedure_codes] of Object.entries(treatment_history)) {
    const new_object: TreatmentHistory = {
      date: formatDate(date),
      procedure_codes: procedure_codes,
    };
    historyObjects.push(new_object);
  }
  const columns: Column<TreatmentHistory>[] = [
    { Header: "Date", accessor: "date" },
    {
      Header: "Procedure Codes",
      accessor: "procedure_codes",
      Cell: ({ value }) => (value == null ? "" : value.join(", ")),
    },
  ];
  const section: Section<TreatmentHistory> = {
    title: "Treatment History",
    columns: columns,
    rows: historyObjects,
  };
  return section;
};

const createSimilarClaimDatesSection = function (
  similar_claim_dates: Array<Date>
): Section<{ date: string }> {
  const dateObjects = similar_claim_dates.map(date => ({
    date: formatDate(date.toISOString().split('T')[0]) || ""
  }));

  const columns: Column<{ date: string }>[] = [
    { Header: "Similar Claim Date", accessor: "date" }
  ];

  const section: Section<{ date: string }> = {
    title: "Similar Claim Dates",
    columns: columns,
    rows: dateObjects,
  };
  return section;
};

const createClaimInfoSection = function (
  claim_info: ClaimInfoUnion,
  section_name: string,
  _claim_number: number
): Section<ClaimInfoUnion> {
  const columns: Array<Column<ClaimInfoUnion>> = Object.keys(claim_info)
    .filter((key) => {
      const value = claim_info[key as keyof ClaimInfoUnion];
      // Include the column if the value is not null/undefined
      if (value == null) {
        return false;
      }
      // Filter out objects (but allow arrays, dates, etc.)
      if (typeof value === "object") {
        if (Array.isArray(value)) {
          return true; // Allow arrays
        }
        // Check if it's a Date object (cast to any to avoid TypeScript error)
        if ((value as any) instanceof Date) {
          return true; // Allow dates
        }
        // It's a plain object, skip it
        return false;
      }
      return true;
    })
    .map((key) => {
      return {
        Header: varNameToTitle(key),
        accessor: key as keyof ClaimInfoUnion,
        Cell: ({ value }: { value: any }) => {
          // Safeguard: Ensure value is a primitive before formatting
          if (typeof value === "object" && value !== null) {
            if (Array.isArray(value)) {
              // Handle arrays by joining them
              return value.join(", ");
            }
            if (value instanceof Date) {
              // Handle dates
              return formatCell(key, value.toISOString());
            }
            // It's a plain object, convert to string
            return JSON.stringify(value);
          }
          return formatCell(key, value);
        },
      };
    });

  // Special handling for code_details array if it exists
  if (claim_info.code_details && claim_info.code_details.length > 0) {
    columns.push({
      Header: "Code Details",
      accessor: "code_details" as keyof ClaimInfoUnion,
      Cell: ({ value }: { value: any }) => {
        if (!value || !Array.isArray(value)) return "";
        return value.map((detail: any) =>
          `${detail.procedure_code}: $${detail.amount_paid || 0}`
        ).join(", ");
      },
    });
  }

  const rows = [claim_info];
  const section: Section<ClaimInfoUnion> = {
    title: section_name,
    columns: columns,
    rows: rows,
  };
  return section;
};
